import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../utils/config';
import { useTranslation } from '../context/TranslationContext';
import './PurchaseHistoryModal.css';

interface PurchaseHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PurchaseRecord {
  _id: string;
  product_name: string;
  product_brand: string;
  product_description: string;
  product_category: string;
  quantity: number;
  unit: string;
  unit_price: number;
  total_price: number;
  currency: string;
  supplier_name: string;
  supplier_email: string;
  supplier_phone: string;
  supplier_address: string;
  supplier_website: string;
  purchase_date: string;
  delivery_time: string;
  notes: string;
  is_ocr_generated: boolean;
}

const PurchaseHistoryModal: React.FC<PurchaseHistoryModalProps> = ({ isOpen, onClose }) => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [purchases, setPurchases] = useState<PurchaseRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchPurchaseHistory();
    }
  }, [isOpen]);

  const fetchPurchaseHistory = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS}/purchase-history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setPurchases(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching purchase history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!token || !searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS}/purchase-history/search?q=${encodeURIComponent(searchQuery)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSearchResults(result.data);
        }
      }
    } catch (error) {
      console.error('Error searching purchase history:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatPrice = (price: number, currency: string) => {
    return `${currency} ${price.toFixed(2)}`;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content purchase-history-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📊 {t('common.purchase_history')}</h2>
          <button className="close-button" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {/* Search Section */}
          <div className="search-section">
            <h3>🔍 {t('common.search_purchase_history')}</h3>
            <div className="search-input-group">
              <input
                type="text"
                placeholder={t('common.search_products_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="search-input"
              />
              <button 
                onClick={handleSearch}
                disabled={isSearching || !searchQuery.trim()}
                className="search-button"
              >
                {isSearching ? t('common.searching_products') : t('common.search')}
              </button>
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="search-results-section">
              <h3>🔍 Search Results for "{searchQuery}"</h3>
              <div className="search-results">
                {searchResults.map((result, index) => (
                  <div key={index} className="search-result-card">
                    <div className="result-header">
                      <h4>{result.product_name}</h4>
                      <span className="product-brand">{result.product_brand}</span>
                    </div>
                    
                    <div className="result-details">
                      <div className="product-info">
                        <p><strong>Category:</strong> {result.product_category}</p>
                        <p><strong>Description:</strong> {result.product_description}</p>
                      </div>
                      
                      <div className="supplier-info">
                        <h5>Supplier: {result.supplier_name}</h5>
                        <p><strong>Email:</strong> {result.supplier_email}</p>
                        {result.supplier_phone && <p><strong>Phone:</strong> {result.supplier_phone}</p>}
                        {result.supplier_website && (
                          <p><strong>Website:</strong> <a href={result.supplier_website} target="_blank" rel="noopener noreferrer">{result.supplier_website}</a></p>
                        )}
                      </div>
                      
                      <div className="purchase-history">
                        <h5>Previous Purchases:</h5>
                        {result.purchases.map((purchase: any, pIndex: number) => (
                          <div key={pIndex} className="purchase-item">
                            <div className="purchase-date">
                              <strong>Date:</strong> {formatDate(purchase.purchase_date)}
                            </div>
                            <div className="purchase-details">
                              <span><strong>Price:</strong> {formatPrice(purchase.unit_price, purchase.currency)}</span>
                              <span><strong>Quantity:</strong> {purchase.quantity} {purchase.unit}</span>
                              <span><strong>Total:</strong> {formatPrice(purchase.total_price, purchase.currency)}</span>
                            </div>
                            {purchase.delivery_time && (
                              <div className="delivery-time">
                                <strong>Delivery:</strong> {purchase.delivery_time}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All Purchase History */}
          <div className="purchase-history-section">
            <h3>📋 All Purchase History</h3>
            {loading ? (
              <div className="loading">Loading purchase history...</div>
            ) : purchases.length > 0 ? (
              <div className="purchase-list">
                {purchases.map((purchase) => (
                  <div key={purchase._id} className="purchase-card">
                    <div className="purchase-header">
                      <h4>{purchase.product_name}</h4>
                      <span className="purchase-date">{formatDate(purchase.purchase_date)}</span>
                    </div>
                    
                    <div className="purchase-details">
                      <div className="product-info">
                        <p><strong>Brand:</strong> {purchase.product_brand}</p>
                        <p><strong>Category:</strong> {purchase.product_category}</p>
                        <p><strong>Quantity:</strong> {purchase.quantity} {purchase.unit}</p>
                      </div>
                      
                      <div className="price-info">
                        <p><strong>Unit Price:</strong> {formatPrice(purchase.unit_price, purchase.currency)}</p>
                        <p><strong>Total Price:</strong> {formatPrice(purchase.total_price, purchase.currency)}</p>
                      </div>
                      
                      <div className="supplier-info">
                        <p><strong>Supplier:</strong> {purchase.supplier_name}</p>
                        <p><strong>Email:</strong> {purchase.supplier_email}</p>
                        {purchase.supplier_phone && <p><strong>Phone:</strong> {purchase.supplier_phone}</p>}
                      </div>
                      
                      {purchase.is_ocr_generated && (
                        <div className="ocr-badge">
                          <span>📄 OCR Generated</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-purchases">
                <p>No purchase history found.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseHistoryModal; 